import { useState, useEffect, useRef } from 'react';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import { sandhillsRequest, focusErroredField, insertURLParameter } from '@Components/Utilities/SandhillsUtility';
import { getExpireTimeUTCStringForCookies, setCookie } from '@Components/Utilities/CookieHelper';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import Calendar from '@Components/MuiWrappers/Calendar';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import Input from '@mui/material/TextField';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { forwardRef } from 'react';

const TextField = loadable(() => import('@Components/Shared/Controls/TextField'));
const SelectField = loadable(() => import('@Components/Shared/Controls/SelectField'));
const MediaUpload = loadable(() => import('@Components/Shared/MediaUpload'));
const StolenAddSuccessModal = loadable(() => import('@Components/Shared/Modals/StolenAddSuccessModal'), true);

const StolenEquipmentVendorView = (props) => {

    const today = new Date();
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const beginDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 0, 0, 0, 0);
    const actions = { add: 0, modify: 1, delete: 2 };
    const stolenEquipmentResults = props.StolenEquipmentResults ? props.StolenEquipmentResults : {};

    const [stolenEquipmentList, setStolenEquipmentList] = useState(stolenEquipmentResults.List || []);
    const [stolenEquipmentCount, setStolenEquipmentCount] = useState(stolenEquipmentResults.Count || 0);
    const [stolenEquipmentCurrentPage, setStolenEquipmentCurrentPage] = useState(stolenEquipmentResults.CurrentPage || 0);
    const [stolenEquipmentTotalPages, setStolenEquipmentTotalPages] = useState(stolenEquipmentResults.TotalPages || 0);
    const [states, setStates] = useState(props.States || []);
    const [categories, setCategories] = useState([]);
    const [media, setMedia] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [disableCategories, setDisableCategories] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [success, setSuccess] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [showInputForm, setShowInputForm] = useState(stolenEquipmentCount > 0 ? false : true);
    const [deletedListingIDs, setDeletedListingIDs] = useState(stolenEquipmentResults.DeletedIDs || []);
    const [showModal, setShowModal] = useState(false);
    const inputReferences = useRef({});
    const routes = useRecoilValue(atoms.routes);

    const [formData, setFormData] = useState({
        Id: 0,
        CategoryId: '',
        Manufacturer: '',
        Model: '',
        Year: '',
        SerialNumberVIN: '',
        BriefDescription: '',
        DetailedDescription: '',
        TheftDetail: '',
        EngineNumber: '',
        StolenBeginDate: beginDate,
        StolenEndDate: endDate,
        StolenCaseNumber: '',
        StolenCity: '',
        StolenState: '',
        StolenCountry: '',
        LEAName: '',
        LEAPhoneNumber: '',
        LEAExtention: '',
        EquipmentOwner: '',
        ShowCompany: false,
        InsuranceClaimNumber: '',
        InsuranceCompanyName: '',
        InsuranceContactName: '',
        InsurancePhone: '',
        InsuranceExtention: '',
        MediaIDs: [],
    });

    useEffect(() => {
        if (props.StolenEquipment && props.StolenEquipment.Id > 0) {
            editStolenEquipment(props.StolenEquipment);
        }
    }, []);

    const displayInputForm = (display) => {
        setShowInputForm(display);
        if (display) {
            scrollToForm();
        } else {
            scrollToStolenListings();
        }
    };

    const scrollToForm = () => {
        setTimeout(() => {
            let rect = document.getElementById('stolen-equipment-form')?.getBoundingClientRect?.();
            if (rect) {
                window.scrollTo({ top: (rect.top + document.body.scrollTop), behavior: 'smooth' });
            }
        }, 100);
    };

    const scrollToStolenListings = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const onBeginDateChange = (date) => {
        setFormData(previousState => {
            return { ...previousState, StolenBeginDate: date };
        });
    };

    const onEndDateChange = (date) => {
        setFormData(previousState => {
            return { ...previousState, StolenEndDate: date };
        });
    };

    const onPageChange = (page) => {
        let stolenEquipmentURLsURL = insertURLParameter(routes.GetStolenEquipmentVendor, "Page", page);
        sandhillsRequest({
            url: stolenEquipmentURLsURL,
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            setStolenEquipmentList(response.List || []);
            setStolenEquipmentCount(response.Count || 0);
            setStolenEquipmentCurrentPage(response.CurrentPage || 0);
            setStolenEquipmentTotalPages(response.TotalPages || 0);
        });
    };

    const getAutoCompleteUrl = (e, url) => {
        return url;
    };
    const getAutoCompleteModelData = (e) => {
        return {
            text: e.target.value,
            categoryIDs: (formData.CategoryId ? getSelectedCategories([formData.CategoryId], formData.CategoryId).join(',') : props.EquipmentDisplayCategoryIds.join(',')),
            manufacturer: formData.Manufacturer
        };
    };
    const getAutoCompleteManuData = (e) => {
        return {
            text: e.target.value,
            categoryIDs: (formData.CategoryId ? getSelectedCategories([formData.CategoryId], formData.CategoryId).join(',') : props.EquipmentDisplayCategoryIds.join(',')),
        };
    };

    const getCategories = (manufacturer, model, category) => {
        if (manufacturer && (!props.IsModelRequired || model)) {
            setCategories([]);
            setDisableCategories(true);
            setLoadingCategories(true);

            sandhillsRequest({
                url: routes.GetStolenEquipmentDrilldown,
                data: {
                    manufacturer: manufacturer,
                    model: model
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((response) => {
                let data = response || [];
                setFormData(previousState => {
                    return { ...previousState, CategoryId: category ? (data.some(cat => cat.Value == category) ? category : '') : '' };
                });

                setCategories(data);
                setDisableCategories(false);
                setLoadingCategories(false);
            });
        } else {
            setFormData(previousState => {
                return { ...previousState, CategoryId: '' };
            });

            setCategories([]);
            setDisableCategories(true);
            setLoadingCategories(false);
        }
    };

    const getSelectedCategories = (categoryIds, currentId) => {
        let selectedCategory = categories.find(cat => cat.Value == currentId);
        if (selectedCategory && selectedCategory.IsParent) {
            categories.forEach(cat => {
                if (cat.ParentValue == selectedCategory.Value) {
                    categoryIds.push(cat.Value);
                    categoryIds = getSelectedCategories(categoryIds, cat.Value);
                }
            });
        }

        return categoryIds;
    };

    const generateKey = (item) => item.ParentName + item.Value + item.Text + item.Disabled + item.IsParent + item.Name + item.ParentValue;

    const editStolenEquipment = (equipment) => {
        let stolenBeginDate = new Date(equipment.StolenBeginDate);
        let utcStolenBeginDate = new Date(stolenBeginDate.getTime() + stolenBeginDate.getTimezoneOffset() * 60000);
        utcStolenBeginDate.setHours(0, 0, 0, 0);

        let stolenEndDate = new Date(equipment.StolenEndDate);
        let utcStolenEndDate = new Date(stolenEndDate.getTime() + stolenEndDate.getTimezoneOffset() * 60000);
        utcStolenEndDate.setHours(0, 0, 0, 0);

        displayInputForm(true);
        setFormData({
            Id: equipment.Id,
            CategoryId: equipment.CategoryId,
            Manufacturer: equipment.Manufacturer,
            Model: equipment.Model,
            Year: equipment.Year,
            SerialNumberVIN: equipment.SerialNumberVIN,
            BriefDescription: equipment.BriefDescription,
            DetailedDescription: equipment.DetailedDescription,
            TheftDetail: equipment.TheftDetail,
            EngineNumber: equipment.EngineNumber,
            StolenBeginDate: utcStolenBeginDate,
            StolenEndDate: utcStolenEndDate,
            StolenCaseNumber: equipment.StolenCaseNumber,
            StolenCity: equipment.StolenCity,
            StolenState: equipment.StolenState,
            StolenCountry: equipment.StolenCountry,
            LEAName: equipment.LEAName,
            LEAPhoneNumber: equipment.LEAPhoneNumber,
            LEAExtention: equipment.LEAExtention,
            EquipmentOwner: equipment.EquipmentOwner,
            ShowCompany: equipment.ShowCompany,
            InsuranceClaimNumber: equipment.InsuranceClaimNumber,
            InsuranceCompanyName: equipment.InsuranceCompanyName,
            InsuranceContactName: equipment.InsuranceContactName,
            InsurancePhone: equipment.InsurancePhone,
            InsuranceExtention: equipment.InsuranceExtention,
            MediaIDs: equipment.MediaIDs,
        });

        setValidationErrors({});
        setSuccess(false);
        setSubmitted(false);

        getCategories(equipment.Manufacturer, equipment.Model, equipment.CategoryId);

        let mediaIds = equipment.MediaIDs && equipment.MediaIDs.length > 0 ? equipment.MediaIDs.join('|') : '';

        sandhillsRequest({
            url: insertURLParameter(routes.GetMedia, "MediaIds", mediaIds),
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            let newMedia = [];
            if (response && response.length > 0) {
                newMedia = response.map(media => {
                    return {
                        src: decodeURIComponent(media.MediaUrl),
                        mediaId: media.MediaId,
                        mediaUrl: decodeURIComponent(media.MediaUrl),
                        fileName: media.MediaFileName,
                        loaded: true
                    };
                });
            }
            setMedia(newMedia);
        });

        sandhillsRequest({
            url: routes.GetStatesByCountry,
            data: {
                'CountryName': equipment.StolenCountry,
                'DefaultText': translator.translate('sandhills.registration.selectastateprovince'),
                'ByStateCode': false
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            setStates(response);
        });
    };

    const addDeletedListing = (currentDeletedID) => {
        let newDeletedIDs = [...deletedListingIDs, currentDeletedID.toString()];
        let expiresOn = getExpireTimeUTCStringForCookies(1);
        let allDeletedIDs = newDeletedIDs.join(',');
        setCookie('DeletedStolenEquipmentIDs', allDeletedIDs, expiresOn);

        if (stolenEquipmentList.length > 0) {
            setStolenEquipmentList(previousState => {
                return [...previousState].map(listing => {
                    return { ...listing, PendingDelete: newDeletedIDs.includes(listing.Id.toString()) };
                });
            });
        }

        setDeletedListingIDs(newDeletedIDs);
    };

    const deleteStolenEquipment = (equipment) => {
        sandhillsRequest({
            url: routes.SaveStolenEquipment,
            method: 'post',
            data: {
                Id: equipment.Id,
                Crmid: equipment.Crmid,
                Action: actions.delete
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then(() => {
            setStolenEquipmentList((previousState) => {
                const stolenEquipmentList = [...previousState].map(item => {
                    if (item.Id == equipment.Id) {
                        addDeletedListing(item.Id);
                    }
                    return item;
                });
                return stolenEquipmentList;
            });
        });
    };

    const saveStolenEquipment = () => {
        let rules = [
            ruleRunner('Manufacturer', translator.translate('sandhills.manufacturer'), ValidationRules.required),
            ruleRunner('CategoryId', translator.translate('sandhills.category'), ValidationRules.required),
            ruleRunner('Year', translator.translate('sandhills.year'), ValidationRules.required),
            ruleRunner('SerialNumberVIN', translator.translate('sandhills.serialnum'), ValidationRules.required),
            ruleRunner('StolenCountry', translator.translate('sandhills.country'), ValidationRules.required),
            ruleRunner('StolenState', translator.translate('sandhills.stateprovince'), ValidationRules.required),
            ruleRunner('StolenCity', translator.translate('sandhills.city'), ValidationRules.required),
            ruleRunner('StolenBeginDate', translator.translate('sandhills.date'), ValidationRules.required),
            ruleRunner('StolenEndDate', translator.translate('sandhills.date'), ValidationRules.required),
            ruleRunner('LEAName', translator.translate('sandhills.name'), ValidationRules.required),
            ruleRunner('LEAPhoneNumber', translator.translate('sandhills.phone'), ValidationRules.required),
            ruleRunner('StolenCaseNumber', translator.translate('sandhills.crm.dscrm.casenumber'), ValidationRules.required)
        ];

        if (props.IsModelRequired) {
            rules.push(ruleRunner('Model', translator.translate('sandhills.model'), ValidationRules.required))
        }

        let errors = run(formData, rules);

        setSuccess(false);
        setSubmitted(false);
        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);

        if (Object.keys(errors).length <= 0) {
            let currentAction = (formData.Id > 0) ? actions.modify : actions.add;
            setIsSubmitting(true);
            sandhillsRequest({
                url: routes.SaveStolenEquipment,
                method: 'post',
                data: {
                    ...formData,
                    StolenBeginDate: formData.StolenBeginDate.toISOString(),
                    StolenEndDate: formData.StolenEndDate.toISOString(),
                    Crmid: props.Crmid,
                    Action: currentAction
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((response) => {
                if (response && response.Success) {
                    clearForm(true);
                    setShowModal(showModal => !showModal);
                } else {
                    setSubmitted(true);
                    setIsSubmitting(false);
                    setValidationErrors(response.ValidationErrors || {});
                    focusErroredField(response.ValidationErrors, inputReferences);
                }
            });
        }
    };

    const clearForm = (success) => {
        setMedia([]);
        setValidationErrors({});
        setIsSubmitting(false);
        setSuccess(success || false);
        setSubmitted(success || false);
        setFormData({
            Id: 0,
            CategoryId: '',
            Manufacturer: '',
            Model: '',
            Year: '',
            SerialNumberVIN: '',
            BriefDescription: '',
            DetailedDescription: '',
            TheftDetail: '',
            EngineNumber: '',
            StolenBeginDate: beginDate,
            StolenEndDate: endDate,
            StolenCaseNumber: '',
            StolenCity: '',
            StolenState: '',
            StolenCountry: '',
            LEAName: '',
            LEAPhoneNumber: '',
            LEAExtention: '',
            EquipmentOwner: '',
            ShowCompany: false,
            InsuranceClaimNumber: '',
            InsuranceCompanyName: '',
            InsuranceContactName: '',
            InsurancePhone: '',
            InsuranceExtention: '',
            MediaIDs: [],
        });

        if (stolenEquipmentCount > 0) {
            displayInputForm(false);
        } else {
            scrollToForm();
        }
    };

    const handleAddAnother = () => {
        displayInputForm(true);
    };

    const countryChange = (e) => {
        let country = e.target.value;
        setFormData(previousState => {
            return { ...previousState, StolenCountry: country };
        });

        sandhillsRequest({
            url: routes.GetStatesByCountry,
            data: {
                'CountryName': country,
                'DefaultText': translator.translate('sandhills.registration.selectastateprovince'),
                'ByStateCode': false
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            setStates(response);
        });
    };

    const textChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    const onModalToggle = () => setShowModal(showModal => !showModal);

    const sx = {
        '& .MuiOutlinedInput-root.Mui-focused': {
            '& > fieldset': {
                borderColor: 'black'
            }
        },
        backgroundColor: 'white',
            input: {
            padding: '5px',
                fontSize: '14px',
                    color: 'black'
        },
        button: {
            margin: '0px'
        },
        svg: {
            width: '15px',
                height: '15px'
        }
    };
   
    return (
        <div className='stolen-equipment-container'>
            {stolenEquipmentCount > 0 &&
                <>
                    <h3 className='uppercase'><TranslationText tKey='Sandhills.ViewMyStolenEquipmentPosts' /></h3>
                <button className='ts-button' id='addAnother' onClick={handleAddAnother}><span>Add Stolen Listing</span><i className='fas fa-plus'></i></button>
                    <div className='stolen-equipment-vendor' id='stolen-equipment-vendor'>
                        <div>
                            <div className='stolen-equipment-section-header'>
                                <div className='stolen-equipment-section-half left'>
                                    <h4><TranslationText tKey='Sandhills.Equipment' /></h4>
                                </div>
                                <div className='stolen-equipment-section-half right'>
                                    <h4>{stolenEquipmentCount}</h4>
                                </div>
                            </div>
                            <div>
                                {stolenEquipmentList.map(equipment =>
                                    <div key={equipment.Id} className={'stolen-equipment-entry'} id={equipment.Id}>
                                        {equipment.PendingDelete && <h2 className='pending-delete'>Pending Delete</h2>}
                                        <div className={'stolen-equipment-info' + (equipment.PendingDelete ? ' disabled' : '')}>
                                            <div className='stolen-equipment-third'>
                                                <img src={equipment.ImgUrl} alt="" />
                                            </div>
                                            <div className='stolen-equipment-two-third'>
                                                <a href={equipment.DetailPageUrl}><h3>{equipment.FormattedTitle}</h3></a>
                                                <div className='stolen-equipment-half'>
                                                    <div>
                                                        <span className='stolen-equipment-entry-info'>
                                                            <strong><TranslationText tKey='Sandhills.StolenLocation' />: </strong>
                                                            {equipment.FormattedStolenLocation}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className='stolen-equipment-entry-info'>
                                                            <strong><TranslationText tKey='Sandhills.Status' />: </strong>
                                                            {equipment.StolenStatus}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className='stolen-equipment-entry-info'>
                                                            <strong><TranslationText tKey='Sandhills.SerialNumber' />: </strong>
                                                            {equipment.SerialNumberVIN}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className='stolen-equipment-half'>
                                                    <div>
                                                        <span className='stolen-equipment-entry-info'>
                                                            <strong><TranslationText tKey='Sandhills.TheftDetails' />: </strong>
                                                            <br />
                                                            {equipment.TheftDetail}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='stolen-equipment-update-delete'>
                                                <span className='update-time'>
                                                    <strong><TranslationText tKey='Sandhills.Updated' />: </strong>
                                                    {equipment.FormattedUpdatedOnTime}
                                                </span>
                                                <span className='stolen-equipment-edit'>
                                                    <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); editStolenEquipment(props); } }} onClick={() => editStolenEquipment(equipment)}><i className='fas fa-edit'></i> <TranslationText tKey='Sandhills.Edit' /></a>
                                                </span>
                                                <span className='stolen-equipment-delete'>
                                                    <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); deleteStolenEquipment(equipment); } }} onClick={() => deleteStolenEquipment(equipment)}><i className='fas fa-trash'></i> <TranslationText tKey='Sandhills.Delete' /></a>
                                                </span>
                                            </div>
                                        </div>
                                    </div>)}
                            </div>
                        </div>
                        {stolenEquipmentTotalPages && stolenEquipmentTotalPages.length > 1 &&
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                            {stolenEquipmentCurrentPage > 1 && <div className='button ts-button' style={{ width: '100px', height: '30px' }} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPageChange(stolenEquipmentCurrentPage - 1); } }} onClick={() => onPageChange(stolenEquipmentCurrentPage - 1)}><TranslationText tKey='Sandhills.Prev' /></div>}
                                <select style={{ margin: '5px 15px 5px 15px', width: '100px', cursor: 'pointer' }} onChange={(e) => onPageChange(e.target.value)} value={stolenEquipmentCurrentPage}>
                                    {stolenEquipmentTotalPages.map(i =>
                                        <option key={i} value={i}>{i}</option>)}
                                </select>
                            {stolenEquipmentCurrentPage < stolenEquipmentTotalPages.length && <div className='button ts-button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPageChange(stolenEquipmentCurrentPage + 1); } }} style={{ width: '100px', height: '30px' }} onClick={() => onPageChange(stolenEquipmentCurrentPage + 1)}><TranslationText tKey='Sandhills.Next' /></div>}
                            </div>}
                    </div>
                </>}
            {props.DealerName && showInputForm &&
                <>
                <h3 className='uppercase mobile-hide'><TranslationText tKey='sandhills.mystolenequipmentlistings' /></h3>
                    <div className={'stolen-equipment-form grey-box' + (isSubmitting ? ' disabled' : '')} id='stolen-equipment-form'>
                        <ValidationErrors errors={validationErrors}/>
                        <span className='stolen-equipment-header capitalize'><TranslationText tKey='Sandhills.TradePubWebSite.MVC.EnterYourStolenEquipment' /></span>
                        <div className='stolen-equipment-input-details'>
                            <p><TranslationText tKey='Sandhills.FillOutAllRequiredFields' /> (marked with <span className='required red' />).</p>
                            <p>
                                <strong>{props.DealerName}</strong><br />
                                {props.DealerContactName && <>{props.DealerContactName}<br /></>}
                                {props.DealerLocation && <>{props.DealerLocation}<br /></>}
                                {props.DealerPhoneNumber && <><TranslationText tKey='Sandhills.Phone' />: {props.DealerPhoneNumber}<br /></>}
                                {props.DealerEmail && <><TranslationText tKey='Sandhills.Email' />: {props.DealerEmail}<br /></>}
                                <label>
                                    <input
                                        type='checkbox'
                                        name='ShowCompany'
                                        checked={formData.ShowCompany}
                                        onChange={(e) => {
                                            let checked = e.target.checked;
                                            setFormData(previousState => {
                                                return { ...previousState, ShowCompany: checked };
                                            })
                                        }}
                                    /> <TranslationText tKey='Sandhills.TradePubWebSite.MVC.DisplayCompanyOnTheListing' />?
                                </label>
                            </p>
                            <div className='stolen-equipment-input-container'>
                                <span className='stolen-equipment-input'>
                                    <TextField
                                        name='Manufacturer'
                                        id='Manufacturer'
                                        value={formData.Manufacturer}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.manufacturer' />}
                                        class='stolen-equipment-input-half'
                                        required={true}
                                        placeholder={translator.translate('sandhills.tradepubwebsite.mvc.starttypingtoselectamanufacturer')}
                                        validationErrors={validationErrors.Manufacturer}
                                        ref={(element) => inputReferences.current[`Manufacturer`] = element}
                                        onFieldChanged={textChange}
                                        onFieldBlur={(e) => getCategories(e.target.value, formData.Model)}
                                        autoComplete={{
                                            ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetManuAutoComplete),
                                            postData: (e) => getAutoCompleteManuData(e)
                                        }}
                                        updateField={(value) => setFormData(previousState => {
                                            return { ...previousState, Manufacturer: value };
                                        })}
                                    />
                                    <TextField
                                        name='Model'
                                        id='Model'
                                        value={formData.Model}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.model' />}
                                        class='stolen-equipment-input-half'
                                        required={props.IsModelRequired}
                                        placeholder={translator.translate('sandhills.tradepubwebsite.mvc.starttypingtoselectamodel')}
                                        validationErrors={validationErrors.Model}
                                        ref={(element) => inputReferences.current[`Model`] = element}
                                        onFieldChanged={textChange}
                                        onFieldBlur={(e) => getCategories(formData.Manufacturer, e.target.value)}
                                        autoComplete={{
                                            ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetModelAutoComplete),
                                            postData: (e) => getAutoCompleteModelData(e)
                                        }}
                                        updateField={(value) => setFormData(previousState => {
                                            return { ...previousState, Model: value };
                                        })}
                                    />
                                </span>
                                <span className='stolen-equipment-input'>
                                    <div className='stolen-equipment-input-full'>
                                        <label htmlFor='CategoryID' className='bold'><TranslationText tKey='sandhills.category' /> <span className='required red' /></label>
                                        <select id='CategoryID' onChange={textChange} name='CategoryId' value={formData.CategoryId} disabled={disableCategories} ref={(element) => inputReferences.current[`CategoryId`] = element}>
                                            {categories && categories.length > 0 ? categories.map(cat =>
                                                <option key={generateKey(cat)} value={cat.Value} disabled={cat.IsParent} className={cat.IsParent ? 'drill-search-parent-option' : 'drill-search-option'}>{cat.Text}</option>) : <option value=''>{loadingCategories ? translator.translate('sandhills.loading') : translator.translate('sandhills.tradesites.mvc.selectacategory')}</option>}
                                        </select>
                                        <span className='error'>{validationErrors.CategoryId}</span>
                                    </div>
                                </span>
                                <span className='stolen-equipment-input'>
                                    <div className='stolen-equipment-input-third'>
                                        <label htmlFor='Year' className='bold'><TranslationText tKey='sandhills.year' /> <span className='required red' /></label>
                                        <select id='Year' onChange={textChange} name='Year' value={formData.Year} ref={(element) => inputReferences.current[`Year`] = element}>
                                            {props.Years && props.Years.length > 0 ? props.Years.map(year =>
                                                <option key={year.Value} value={year.Value}>{year.Text}</option>) : <option value=''>{translator.translate('sandhills.tradepubwebsite.mvc.selectayear')}</option>}
                                        </select>
                                        <span className='error'>{validationErrors.Year}</span>
                                    </div>
                                    <TextField
                                        name='SerialNumberVIN'
                                        value={formData.SerialNumberVIN}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.serialnum' />}
                                        class='stolen-equipment-input-third'
                                        required={true}
                                        validationErrors={validationErrors.SerialNumberVIN}
                                        ref={(element) => inputReferences.current[`SerialNumberVIN`] = element}
                                        onFieldChanged={textChange}
                                    />
                                    <TextField
                                        name='EngineNumber'
                                        value={formData.EngineNumber}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.enginenumber' />}
                                        class='stolen-equipment-input-third'
                                        required={false}
                                        validationErrors={validationErrors.EngineNumber}
                                        ref={(element) => inputReferences.current[`EngineNumber`] = element}
                                        onFieldChanged={textChange}
                                    />
                                </span>
                                <span className='stolen-equipment-input'>
                                    <div className='stolen-equipment-input-full'>
                                        <label htmlFor='BriefDescription' className='bold'><TranslationText tKey='Sandhills.BriefDescription' /></label>
                                        <input id='BriefDescription' type='text' onChange={textChange} name='BriefDescription' value={formData.BriefDescription} />
                                        <span className='small-text'><TranslationText tKey='Sandhills.TradePubWebSite.MVC.UniqueFeaturesOfMachine' /></span>
                                    </div>
                                </span>
                                <span className='stolen-equipment-input'>
                                    <div className='stolen-equipment-input-full'>
                                        <label htmlFor='DetailedDescription' className='bold'><TranslationText tKey='Sandhills.DetailedDescription' /></label>
                                        <textarea id='DetailedDescription' onChange={textChange} name='DetailedDescription' value={formData.DetailedDescription} placeholder={translator.translate('Sandhills.ExtendedDescriptionOfEquipment')} />
                                        <span className='small-text'><TranslationText tKey='Sandhills.TradePubWebSite.MVC.ExtendedDescriptionOfEquipment' /></span>
                                    </div>
                                </span>
                                <br />
                                <span className='stolen-equipment-subtitle'><p><TranslationText tKey='Sandhills.TradePubWebSite.MVC.WhatInsuranceAgencyWasThisReportedTo' /></p></span>
                                <span className='stolen-equipment-input'>
                                    <TextField
                                        name='InsuranceCompanyName'
                                        value={formData.InsuranceCompanyName}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.companyname' />}
                                        class='stolen-equipment-input-half'
                                        required={false}
                                        validationErrors={validationErrors.InsuranceCompanyName}
                                        ref={(element) => inputReferences.current[`InsuranceCompanyName`] = element}
                                        onFieldChanged={textChange}
                                    />
                                    <TextField
                                        name='InsuranceContactName'
                                        value={formData.InsuranceContactName}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.contactname' />}
                                        class='stolen-equipment-input-half'
                                        required={false}
                                        validationErrors={validationErrors.InsuranceContactName}
                                        ref={(element) => inputReferences.current[`InsuranceContactName`] = element}
                                        onFieldChanged={textChange}
                                    />
                                </span>
                                <span className='stolen-equipment-input'>
                                    <div className='stolen-equipment-input-half'>
                                        <TextField
                                            name='InsurancePhone'
                                            value={formData.InsurancePhone}
                                            type='text'
                                            label={<TranslationText tKey='sandhills.phone' />}
                                            class='stolen-equipment-input-phone'
                                            required={false}
                                            validationErrors={validationErrors.InsurancePhone}
                                            ref={(element) => inputReferences.current[`InsurancePhone`] = element}
                                            onFieldChanged={textChange}
                                        />
                                        <TextField
                                            name='InsuranceExtention'
                                            value={formData.InsuranceExtention}
                                            type='text'
                                            label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.extension' />}
                                            class='stolen-equipment-input-extension'
                                            required={false}
                                            validationErrors={validationErrors.InsuranceExtention}
                                            ref={(element) => inputReferences.current[`InsuranceExtention`] = element}
                                            onFieldChanged={textChange}
                                        />
                                    </div>
                                    <TextField
                                        name='InsuranceClaimNumber'
                                        value={formData.InsuranceClaimNumber}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.insuranceclaimnumber' />}
                                        class='stolen-equipment-input-half'
                                        required={false}
                                        validationErrors={validationErrors.InsuranceClaimNumber}
                                        ref={(element) => inputReferences.current[`InsuranceClaimNumber`] = element}
                                        onFieldChanged={textChange}
                                    />
                                </span>
                                <br />
                                <span className='stolen-equipment-subtitle'><p><TranslationText tKey='Sandhills.TradePubWebSite.MVC.WhereWasThisMachineStolenFrom' /></p></span>
                                <span className='stolen-equipment-input'>
                                    <div className='stolen-equipment-input-full'>
                                        <label htmlFor='EquipmentOwner' className='bold'><TranslationText tKey='Sandhills.TradePubWebSite.MVC.EquipmentOwner' /></label>
                                        <input id='EquipmentOwner' type='text' onChange={textChange} name='EquipmentOwner' value={formData.EquipmentOwner} />
                                        <span className='small-text'><TranslationText tKey='Sandhills.TradePubWebSite.MVC.IfYouAreSubmittingForSomeoneElse' /></span>
                                    </div>
                                </span>
                                <span className='stolen-equipment-input'>
                                    {props.Countries && props.Countries.length > 1 &&
                                        <SelectField
                                            id='StolenCountry'
                                            name='StolenCountry'
                                            value={formData.StolenCountry}
                                            options={props.Countries}
                                            label={<TranslationText tKey='sandhills.country' />}
                                            class='stolen-equipment-input-third'
                                            required={true}
                                            validationErrors={validationErrors.StolenCountry}
                                            ref={(element) => inputReferences.current[`StolenCountry`] = element}
                                            onFieldChanged={countryChange}
                                        />}
                                    {states.length > 1 ?
                                        <SelectField
                                            id='StolenState'
                                            name='StolenState'
                                            value={formData.StolenState}
                                            options={states}
                                            label={<TranslationText tKey='sandhills.stateprovince' />}
                                            class='stolen-equipment-input-third'
                                            required={true}
                                            validationErrors={validationErrors.StolenState}
                                            ref={(element) => inputReferences.current[`StolenState`] = element}
                                            onFieldChanged={textChange}
                                        />
                                        :
                                        <TextField
                                            id='StolenState'
                                            name='StolenState'
                                            value={formData.StolenState}
                                            type='text'
                                            label={<TranslationText tKey='sandhills.stateprovince' />}
                                            class='stolen-equipment-input-third'
                                            required={true}
                                            validationErrors={validationErrors.StolenState}
                                            ref={(element) => inputReferences.current[`StolenState`] = element}
                                            onFieldChanged={textChange}
                                        />}
                                    <TextField
                                        id='StolenCity'
                                        name='StolenCity'
                                        value={formData.StolenCity}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.city' />}
                                        class='stolen-equipment-input-third'
                                        required={true}
                                        validationErrors={validationErrors.StolenCity}
                                        ref={(element) => inputReferences.current[`StolenCity`] = element}
                                        onFieldChanged={textChange}
                                    />
                                </span>
                                <span className='stolen-equipment-input'>
                                    <div className='stolen-equipment-input-half stolen-equipment-input-date'>
                                        <label htmlFor='stolen-begin-date' className='bold'><TranslationText tKey='Sandhills.TradePubWebSite.MVC.DateStolenBetween' /> <span className='required red' /></label>
                                        <Calendar
                                            id='stolen-begin-date'
                                            value={formData.StolenBeginDate}
                                            onChange={onBeginDateChange}
                                            maxDate={formData.StolenEndDate}
                                            disableFuture
                                            inputFormat='MMM do, yyyy'
                                            InputAdornmentProps={{
                                                sx: {
                                                    margin: '0px'
                                                }
                                            }}
                                            sx={sx}
                                        />
                                        <span className='error'>{validationErrors.StolenBeginDate}</span>
                                    </div>
                                    <div className='stolen-equipment-input-half stolen-equipment-input-date'>
                                        <label htmlFor='stolen-end-date' className='bold'><TranslationText tKey='Sandhills.TradePubWebSite.MVC.And' /> <span className='required red' /></label>
                                        <Calendar
                                            id='stolen-end-date'
                                            value={formData.StolenEndDate}
                                            onChange={onEndDateChange}
                                            minDate={formData.StolenBeginDate}
                                            disableFuture
                                            inputFormat='MMM do, yyyy'
                                            InputAdornmentProps={{
                                                sx: {
                                                    margin: '0px'
                                                }
                                            }}
                                            sx={sx}
                                        />
                                        <span className='error'>{validationErrors.StolenEndDate}</span>
                                    </div>
                                </span>
                                <br />
                                <span className='stolen-equipment-subtitle'><p><TranslationText tKey='Sandhills.TradePubWebSite.MVC.WhatLawEnforcementAgencyReported' /></p></span>
                                <span className='stolen-equipment-input'>
                                    <TextField
                                        name='LEAName'
                                        value={formData.LEAName}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.name' />}
                                        class='stolen-equipment-input-full'
                                        required={true}
                                        validationErrors={validationErrors.LEAName}
                                        ref={(element) => inputReferences.current[`LEAName`] = element}
                                        onFieldChanged={textChange}
                                    />
                                </span>
                                <span className='stolen-equipment-input'>
                                    <div className='stolen-equipment-input-half'>
                                        <TextField
                                            name='LEAPhoneNumber'
                                            value={formData.LEAPhoneNumber}
                                            type='text'
                                            label={<TranslationText tKey='sandhills.phone' />}
                                            class='stolen-equipment-input-phone'
                                            required={true}
                                            validationErrors={validationErrors.LEAPhoneNumber}
                                            ref={(element) => inputReferences.current[`LEAPhoneNumber`] = element}
                                            onFieldChanged={textChange}
                                        />
                                        <TextField
                                            name='LEAExtention'
                                            value={formData.LEAExtention}
                                            type='text'
                                            label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.extension' />}
                                            class='stolen-equipment-input-extension'
                                            required={false}
                                            validationErrors={validationErrors.LEAExtention}
                                            ref={(element) => inputReferences.current[`LEAExtention`] = element}
                                            onFieldChanged={textChange}
                                        />
                                    </div>
                                    <TextField
                                        name='StolenCaseNumber'
                                        value={formData.StolenCaseNumber}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.crm.dscrm.casenumber' />}
                                        class='stolen-equipment-input-half'
                                        required={true}
                                        placeholder={translator.translate('sandhills.avalidcasenumberisrequired')}
                                        validationErrors={validationErrors.StolenCaseNumber}
                                        ref={(element) => inputReferences.current[`StolenCaseNumber`] = element}
                                        onFieldChanged={textChange}
                                    />
                                </span>
                                <span className='stolen-equipment-input'>
                                    <TextField
                                        name='TheftDetail'
                                        value={formData.TheftDetail}
                                        type='textarea'
                                        label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.detailsoftheft' />}
                                        class='stolen-equipment-input-full'
                                        required={false}
                                        validationErrors={validationErrors.TheftDetail}
                                        ref={(element) => inputReferences.current[`TheftDetail`] = element}
                                        onFieldChanged={textChange}
                                    />
                                </span>
                                <div className='stolen-equipment-subtitle'>
                                    <p><TranslationText tKey='Sandhills.TradePubWebSite.MVC.IfYouHavePhotosOfTheStolenEquipment' />.</p>
                                    <p><TranslationText tKey='Sandhills.TradePubWebSite.MVC.IfYouDoNotHavePhotosOfTheStolenEquipment' />.</p>
                                    <p><TranslationText tKey='Sandhills.TradePubWebSite.MVC.SendPhotosInJpgFormat' />.</p>
                                </div>
                                <div className='stolen-equipment-notice'><TranslationText tKey='Sandhills.TradePubWebSite.MVC.DoNotSubmitPhotoUnlessYouHaveRightsToUse' /></div>
                                <span className='stolen-equipment-input'>
                                    <MediaUpload
                                        id='media'
                                        name='media'
                                        buttonText={<TranslationText tKey='sandhills.choosefiles' />}
                                        mediaType='Photo'
                                        containerClassName='stolen-equipment-input-full'
                                        label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.photoupload' />}
                                        maxPhotoUpload={5}
                                        media={media}
                                        onChange={(media) => {
                                            setMedia(media);
                                            setFormData(previousState => {
                                                return { ...previousState, MediaIDs: media.map(file => file.mediaId) };
                                            });
                                        }}
                                    />
                                </span>
                                <span className='stolen-equipment-input'>
                                    {submitted && !success && <div className='error'><TranslationText tKey='Sandhills.AnErrorOccuredPleaseTryAgain' /></div>}
                                </span>
                                <span className='stolen-equipment-input'>
                                    <div className='error'>{validationErrors.Failure}</div>
                                </span>
                            </div>
                            <div className='stolen-equipment-btn-container'>
                            <button className={'ts-button save-btn' + (isSubmitting ? ' disabled-btn' : '')} disabled={isSubmitting} onClick={saveStolenEquipment}><TranslationText tKey='sandhillssystem.save' /></button>
                            <button className='ts-button cancel-btn' onClick={() => clearForm()}><TranslationText tKey='sandhills.cancel' /></button>
                            </div>
                        <div dangerouslySetInnerHTML={{ __html: props.CAPrivacyPolicyNotice }} style={{ 'font-size': '1em' }} />
                        </div>
                    </div>
                    {isSubmitting && <LoadingRing backdrop />}
                </>}
            {showModal &&
                <StolenAddSuccessModal
                toggleModal={onModalToggle}
                />
            }
        </div>
    );
};

export default StolenEquipmentVendorView;
global.StolenEquipmentVendorView = StolenEquipmentVendorView;